// Reads VITE_AUTO_EMAIL from the build-time env. When present, callers like
// signup(), forgotPassword(), and verifyEmail() will pass these template
// message_ids as per-call overrides so uploaded templates render instead of
// the service defaults.
//
// Expected shape (lenient JS-object syntax allowed in .env*):
//   VITE_AUTO_EMAIL = { signup_confirmation: "<mid>", welcome: "<mid>", verification: "<mid>", forgot_password: "<mid>" }
//
// `verification` is the e-mail verification template (verifyEmail); `forgot_password` is the
// forgot password template (forgotPassword), its own kind since 2026-10. Any missing key is
// simply omitted. If the env var is absent or malformed, the helpers return undefined and
// callers behave exactly as before.

type AutoEmailConfig = {
    signup_confirmation?: string;
    welcome?: string;
    verification?: string;
    forgot_password?: string;
};

function parseAutoEmail(): AutoEmailConfig {
    const raw = (import.meta as any).env?.VITE_AUTO_EMAIL;
    if (!raw || typeof raw !== "string") return {};

    const trimmed = raw.trim();
    if (!trimmed) return {};

    let parsed: any = null;
    // Try strict JSON first.
    try {
        parsed = JSON.parse(trimmed);
    } catch {
        // Fall back to the lenient JS-object form used in the dev .env file
        // ({ key: "value" } with unquoted keys). new Function is fine here
        // because the value is build-time config the developer authored.
        try {
            parsed = new Function(`return (${trimmed});`)();
        } catch {
            parsed = null;
        }
    }

    if (!parsed || typeof parsed !== "object") return {};

    const result: AutoEmailConfig = {};
    if (typeof parsed.signup_confirmation === "string" && parsed.signup_confirmation) {
        result.signup_confirmation = parsed.signup_confirmation;
    }
    if (typeof parsed.welcome === "string" && parsed.welcome) {
        result.welcome = parsed.welcome;
    }
    if (typeof parsed.verification === "string" && parsed.verification) {
        result.verification = parsed.verification;
    }
    if (typeof parsed.forgot_password === "string" && parsed.forgot_password) {
        result.forgot_password = parsed.forgot_password;
    }
    return result;
}

const AUTO_EMAIL: AutoEmailConfig = parseAutoEmail();

/** Returns `{ template: { signup_confirmation, welcome } }` if either mid is
 *  configured, otherwise undefined. Spread into the `options` argument of
 *  `skapi.signup(params, options)`. */
export function signupTemplateOption(): { template: { signup_confirmation?: string; welcome?: string } } | undefined {
    const tpl: { signup_confirmation?: string; welcome?: string } = {};
    if (AUTO_EMAIL.signup_confirmation) tpl.signup_confirmation = AUTO_EMAIL.signup_confirmation;
    if (AUTO_EMAIL.welcome) tpl.welcome = AUTO_EMAIL.welcome;
    return Object.keys(tpl).length ? { template: tpl } : undefined;
}

/** Returns `{ template: { verification } }` if a verification mid is
 *  configured, otherwise undefined. Spread into the second arg of
 *  `skapi.verifyEmail(form, options)`. Not for forgotPassword(): the password
 *  reset e-mail is its own template kind (forgotPasswordTemplateOption). */
export function verificationTemplateOption(): { template: { verification: string } } | undefined {
    if (!AUTO_EMAIL.verification) return undefined;
    return { template: { verification: AUTO_EMAIL.verification } };
}

/** Returns `{ template: { forgot_password } }` if a forgot password mid is
 *  configured, otherwise undefined. Spread into the second arg of
 *  `skapi.forgotPassword(form, options)`. */
export function forgotPasswordTemplateOption(): { template: { forgot_password: string } } | undefined {
    if (!AUTO_EMAIL.forgot_password) return undefined;
    return { template: { forgot_password: AUTO_EMAIL.forgot_password } };
}
